"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { track } from "../core/client";
import {
  getConfig,
  shouldSample,
  checkAndIncrementLimit,
} from "../core/config";
import { getSessionId } from "../core/ids";
import { TrackedLink, type ItemSpec } from "./link";

/**
 * 리스트 노출 추적 컨텍스트
 */
interface ListContextValue {
  listId: string;
  totalCount?: number;
  impressionCount: number;
  registerItem: (element: HTMLElement, itemData: any) => void;
  unregisterItem: (element: HTMLElement) => void;
}

const ListContext = createContext<ListContextValue | null>(null);

/**
 * 추적 대상 아이템 정보
 */
interface TrackedItem {
  element: HTMLElement;
  data: any;
  observed: boolean;
  impressed: boolean;
}

/**
 * 리스트 추적 컴포넌트
 *
 * 단순화된 방식:
 * - 리스트 컨테이너가 viewport에 노출되면
 * - 등록된 아이템 중 상위 15개를 자동으로 노출 처리
 */
export interface TrackedListProps {
  listId: string;
  totalCount?: number;
  children: React.ReactNode;
  maxItems?: number; // 최대 노출 아이템 수 (기본값: 15)
}

export function TrackedList({
  listId,
  totalCount,
  children,
  maxItems = 15,
}: TrackedListProps) {
  const [impressionCount, setImpressionCount] = useState(0);
  const itemsRef = useRef<TrackedItem[]>([]); // 🆕 순서 보장을 위해 배열로 변경
  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasTrackedRef = useRef(false); // 🆕 한번만 추적하도록
  const mountTimeRef = useRef<number>(Date.now());

  // 리스트 컨테이너 관찰 설정
  useEffect(() => {
    const config = getConfig();
    if (!config.autoTracking.impressions) {
      // console.log(`❌ [${listId}] autoTracking.impressions is disabled`);
      return;
    }

    if (!containerRef.current) {
      console.warn(`⚠️ [${listId}] containerRef not set`);
      return;
    }

    // console.log(
    //   `🎬 [${listId}] Creating IntersectionObserver for list container`
    // );

    const container = containerRef.current;

    // 리스트 컨테이너만 관찰
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 리스트가 viewport에 노출되고, 아직 추적하지 않았으면
          if (entry.isIntersecting && !hasTrackedRef.current) {
            // console.log(
            //   `✅ [${listId}] List container is visible (via observer), tracking top ${maxItems} items`
            // );
            hasTrackedRef.current = true;

            // 약간의 딜레이를 줘서 아이템 등록 완료를 기다림
            setTimeout(() => {
              processListImpression();
            }, 100);
          }
        });
      },
      {
        threshold: 0.1, // 리스트의 10%만 보여도 노출로 간주
        rootMargin: "50px",
      }
    );

    observerRef.current.observe(container);

    // 🆕 초기 상태 체크: 이미 viewport에 있으면 IntersectionObserver가 콜백을 호출하지 않을 수 있음
    // 특히 모달 안에서 렌더링되는 경우 대응
    const checkInitialVisibility = () => {
      if (hasTrackedRef.current) {
        // console.log(`⏭️ [${listId}] Already tracked, skipping initial check`);
        return;
      }

      const rect = container.getBoundingClientRect();
      const isVisible =
        rect.top < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.right > 0;

      // console.log(`🔍 [${listId}] Initial visibility check:`, {
      //   rect: {
      //     top: rect.top,
      //     bottom: rect.bottom,
      //     left: rect.left,
      //     right: rect.right,
      //   },
      // });

      if (isVisible) {
        // console.log(
        // `✅ [${listId}] List container is already visible (initial check), tracking top ${maxItems} items`
        // );
        hasTrackedRef.current = true;

        // 추가 딜레이로 아이템 등록 보장
        setTimeout(() => {
          processListImpression();
        }, 150);
      } else {
        // console.log(
        //   `⏳ [${listId}] List not visible yet, waiting for IntersectionObserver`
        // );
      }
    };

    // 여러 타이밍에 체크 (모달 애니메이션 대응)
    setTimeout(checkInitialVisibility, 100);
    setTimeout(checkInitialVisibility, 300);
    setTimeout(checkInitialVisibility, 500);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, maxItems]);

  // 리스트 노출 처리 (상위 maxItems개 아이템만)
  const processListImpression = useCallback(() => {
    const items = itemsRef.current;

    if (items.length === 0) {
      console.warn(`⚠️ [${listId}] No items registered yet`);
      return;
    }

    // 샘플링 체크
    if (!shouldSample("impression")) {
      console.warn(`❌ [${listId}] Dropped by sampling`);
      return;
    }

    // 세션 한도 체크
    const sessionId = getSessionId();
    if (!checkAndIncrementLimit("impression", sessionId)) {
      console.warn(`❌ [${listId}] Dropped by session limit`);
      return;
    }

    // Above the Fold 판단: 마운트 후 800ms 이내면 true
    const timeSinceMount = Date.now() - mountTimeRef.current;
    const aboveFold = timeSinceMount < 800;

    // 상위 maxItems개만 선택
    const itemsToTrack = items.slice(0, maxItems);

    // console.log(
    //   `🔄 [${listId}] Processing ${itemsToTrack.length} items (max: ${maxItems}, total: ${items.length}), aboveFold: ${aboveFold}`
    // );

    // kind별로 아이템 그룹화
    const groupedItems: Record<string, TrackedItem[]> = {};

    itemsToTrack.forEach((item) => {
      if (item.impressed) {
        console.warn(`  ⚠️ [${listId}] Item already impressed, skipping`);
        return;
      }

      const { kind } = item.data;
      if (!kind) {
        console.warn(`  ⚠️ [${listId}] Item missing kind:`, item.data);
        return;
      }

      if (!groupedItems[kind]) {
        groupedItems[kind] = [];
      }
      groupedItems[kind].push(item);
      item.impressed = true; // 노출 완료 표시
    });

    // 배치 전송: stock
    if (groupedItems["stock"] && groupedItems["stock"].length > 0) {
      const stockItems = groupedItems["stock"].map((item, index) => ({
        ticker: item.data.ticker,
        market: item.data.market,
        rank_pos: item.data.rank !== undefined ? item.data.rank : index,
      }));

      // console.log(
      //   `📤 [${listId}] Tracking stock_impression: ${stockItems.length} items`,
      //   stockItems.map((i) => i.ticker)
      // );

      track("stock_impression", {
        list_id: listId,
        items: stockItems,
        total_count: totalCount || stockItems.length,
        visible_above_the_fold: aboveFold,
      });

      setImpressionCount((prev) => prev + groupedItems["stock"].length);
    }

    // 배치 전송: strategy
    if (groupedItems["strategy"] && groupedItems["strategy"].length > 0) {
      const strategyItems = groupedItems["strategy"].map((item, index) => ({
        strategy_id: item.data.strategy_id,
        market: item.data.market,
        rank_pos: item.data.rank !== undefined ? item.data.rank : index,
      }));

      // console.log(
      //   `📤 [${listId}] Tracking strategy_impression: ${strategyItems.length} items`,
      //   strategyItems.map((i) => i.strategy_id)
      // );

      track("strategy_impression", {
        list_id: listId,
        items: strategyItems,
        total_count: totalCount || strategyItems.length,
        visible_above_the_fold: aboveFold,
      });

      setImpressionCount((prev) => prev + groupedItems["strategy"].length);
    }

    // 배치 전송: report
    if (groupedItems["report"] && groupedItems["report"].length > 0) {
      const reportItems = groupedItems["report"].map((item, index) => ({
        report_id: item.data.report_id,
        rank_pos: item.data.rank !== undefined ? item.data.rank : index,
        trace_id: item.data.trace_id,
        topic_cluster: item.data.topic_cluster,
      }));

      // console.log(
      //   `📤 [${listId}] Tracking report_impression: ${reportItems.length} items`,
      //   reportItems.map((i) => i.report_id)
      // );

      track("report_impression", {
        list_id: listId,
        items: reportItems,
        total_count: totalCount || reportItems.length,
        visible_above_the_fold: aboveFold,
      });

      setImpressionCount((prev) => prev + groupedItems["report"].length);
    }

    if (groupedItems["newsrader"] && groupedItems["newsrader"].length > 0) {
      const newsItems = groupedItems["newsrader"].map((item, index) => ({
        newsrader_id: item.data.newsrader_id?.toString() || item.data.ticker, // newsrader_id를 news_id로 매핑
        market: "kr", // 한국 시장
        ticker: item.data.ticker,
        rank_pos: item.data.rank !== undefined ? item.data.rank : index,
      }));

      // console.log(
      //   `📤 [${listId}] Tracking news_rader_impression: ${newsItems.length} items`,
      //   newsItems.map((i) => i.newsrader_id)
      // );

      track("news_rader_impression", {
        list_id: listId,
        items: newsItems,
        total_count: totalCount || newsItems.length,
        visible_above_the_fold: aboveFold,
      });

      setImpressionCount((prev) => prev + groupedItems["newsrader"].length);
    }
  }, [listId, totalCount, maxItems, setImpressionCount]);

  // 아이템 등록 (순서대로 배열에 추가)
  const registerItem = useCallback(
    (element: HTMLElement, itemData: any) => {
      const item: TrackedItem = {
        element,
        data: itemData,
        observed: false,
        impressed: false,
      };

      // 중복 체크 (이미 등록된 element인지)
      const existingIndex = itemsRef.current.findIndex(
        (i) => i.element === element
      );

      if (existingIndex >= 0) {
        // console.log(
        //   `⚠️ [${listId}] Item already registered, skipping:`,
        //   itemData.ticker || itemData.report_id || itemData.strategy_id
        // );
        return;
      }

      // 배열 끝에 추가 (등록 순서 보장)
      itemsRef.current.push(item);

      // console.log(
      //   `🔧 [${listId}] registerItem:`,
      //   itemData.ticker || itemData.report_id || itemData.strategy_id,
      //   `| Total items now: ${itemsRef.current.length}`
      // );
    },
    [listId]
  );

  // 아이템 해제
  const unregisterItem = useCallback(
    (element: HTMLElement) => {
      const index = itemsRef.current.findIndex((i) => i.element === element);

      if (index < 0) {
        // console.log(`⚠️ [${listId}] Item not found, already unregistered`);
        return;
      }

      // console.log(`🗑️ [${listId}] Unregistering item at index ${index}`);
      itemsRef.current.splice(index, 1);
    },
    [listId]
  );

  const contextValue: ListContextValue = useMemo(
    () => ({
      listId,
      totalCount,
      impressionCount,
      registerItem,
      unregisterItem,
    }),
    [listId, totalCount, impressionCount, registerItem, unregisterItem]
  );

  return (
    <ListContext.Provider value={contextValue}>
      <div ref={containerRef}>{children}</div>
    </ListContext.Provider>
  );
}

/**
 * 리스트 컨텍스트 훅
 */
export function useListContext() {
  const context = useContext(ListContext);
  if (!context) {
    throw new Error("useListContext must be used within TrackedList");
  }
  return context;
}

/**
 * 노출/클릭 추적 아이템 데이터
 * Discriminated Union으로 타입 안전성 확보
 */
export type ImpressionItemData =
  | {
      kind: "stock";
      ticker: string;
      market: "kr" | "us";
      rank?: number;
      cat?: "stock" | "index" | "crypto";
      search_intent_id?: string;
    }
  | {
      kind: "report";
      report_id: number;
      rank?: number;
      topic_cluster?: string;
      trace_id?: string;
      search_intent_id?: string;
    }
  | {
      kind: "newsrader";
      ticker: string;
      newsrader_id: number;
      rank?: number;
    }
  | {
      kind: "strategy";
      strategy_id: string;
      market?: "kr" | "us";
      rank?: number;
    };

/**
 * 아이템 노출 추적 훅 (단순화 버전)
 * - 리스트 컨테이너가 노출되면 상위 N개 아이템이 자동으로 추적됨
 * - 개별 아이템은 등록만 하면 됨
 */
export function useImpression(itemData: ImpressionItemData) {
  const listContext = useListContext();
  const elementRef = useRef<Element | null>(null);
  const registeredRef = useRef(false);

  const setRef = useCallback(
    (element: Element | null) => {
      // 동일한 요소인 경우 스킵
      if (elementRef.current === element) {
        return;
      }

      // 기존 요소 해제
      if (elementRef.current && elementRef.current !== element) {
        listContext.unregisterItem(elementRef.current as HTMLElement);
        registeredRef.current = false;
      }

      // 새 요소 등록
      if (element && !registeredRef.current) {
        listContext.registerItem(element as HTMLElement, itemData);
        registeredRef.current = true;
      }

      elementRef.current = element;
    },
    [listContext, itemData]
  );

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (elementRef.current && registeredRef.current) {
        listContext.unregisterItem(elementRef.current as HTMLElement);
        registeredRef.current = false;
      }
    };
  }, [listContext]);

  return { ref: setRef };
}

/**
 * 리스트 아이템 래퍼 컴포넌트
 *
 * @example
 * ```tsx
 * // Impression only
 * <TrackedListItem itemData={{ kind: "stock", ticker: "AAPL", market: "us" }}>
 *   <div>Apple Inc.</div>
 * </TrackedListItem>
 *
 * // Impression + Click
 * <TrackedListItem
 *   itemData={{ kind: "stock", ticker: "AAPL", market: "us", rank: 0 }}
 *   href="/detail/AAPL"
 *   asTrace={true}
 * >
 *   <div>Apple Inc.</div>
 * </TrackedListItem>
 * ```
 */
export interface TrackedListItemProps
  extends React.HTMLAttributes<HTMLElement> {
  itemData: ImpressionItemData;
  href?: string; // 클릭 추적용 URL
  asTrace?: boolean; // trace_id 자동 생성 (기본값: true)
  children: React.ReactNode;
  as?: keyof JSX.IntrinsicElements;
}

export function TrackedListItem({
  itemData,
  href,
  asTrace = true,
  children,
  as = "div",
  ...props
}: TrackedListItemProps) {
  const { ref } = useImpression(itemData);
  const listContext = useListContext();

  // href가 있으면 TrackedLink로 래핑 (클릭 추적)
  if (href) {
    // itemData를 ItemSpec으로 변환
    const itemspec = convertToItemSpec(itemData);
    return (
      <TrackedLink
        href={href}
        asTrace={asTrace}
        fromListId={listContext?.listId}
        itemspec={itemspec}
        ref={ref}
        {...props}
      >
        {children}
      </TrackedLink>
    );
  }

  // href 없으면 일반 요소 (impression만 추적)
  const Component = as as any;
  return React.createElement(Component, { ...props, ref }, children);
}

/**
 * ImpressionItemData를 ItemSpec으로 변환
 */
function convertToItemSpec(itemData: ImpressionItemData): ItemSpec {
  switch (itemData.kind) {
    case "stock":
      return {
        kind: "stock",
        ticker: itemData.ticker,
        market: itemData.market,
        cat: itemData.cat,
        rank: itemData.rank,
        search_intent_id: itemData.search_intent_id,
      };
    case "report":
      return {
        kind: "report",
        report_id: itemData.report_id,
        rank: itemData.rank,
        search_intent_id: itemData.search_intent_id,
      };
    case "newsrader":
      return {
        kind: "newsrader",
        ticker: itemData.ticker,
        newsrader_id: itemData.newsrader_id,
        rank: itemData.rank,
      };
    case "strategy":
      return {
        kind: "strategy",
        strategy_id: itemData.strategy_id,
        rank: itemData.rank,
      };
    default:
      throw new Error(`Unknown itemData kind: ${(itemData as any).kind}`);
  }
}

/**
 * data-track-item 속성을 가진 자동 추적
 */
export function useAutoImpression(listId?: string) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const config = getConfig();
    if (!config.autoTracking.impressions || !containerRef.current) return;

    const container = containerRef.current;
    const sessionId = getSessionId();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const element = entry.target as HTMLElement;
            const trackData = element.dataset.trackItem;

            if (!trackData) return;

            try {
              const itemData = JSON.parse(trackData);

              // 이미 추적된 요소는 스킵
              if (element.dataset.tracked === "true") return;

              // 샘플링 및 한도 체크
              if (
                !shouldSample("impression") ||
                !checkAndIncrementLimit("impression", sessionId)
              ) {
                return;
              }

              // 노출 이벤트 전송
              const eventName = `${itemData.kind}_impression`;
              track(eventName as any, {
                list_id: listId || "auto",
                ...itemData,
                visible_ratio: Math.round(entry.intersectionRatio * 100) / 100,
              });

              // 추적 완료 표시
              element.dataset.tracked = "true";
            } catch (error) {
              console.warn("Failed to parse track-item data:", trackData);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    // data-track-item 속성을 가진 모든 요소 관찰
    const trackItems = container.querySelectorAll("[data-track-item]");
    trackItems.forEach((item) => {
      observerRef.current?.observe(item);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [listId]);

  return { containerRef };
}

/**
 * 노출 통계 훅
 */
export function useImpressionStats(listId: string) {
  const [stats, setStats] = useState({
    totalItems: 0,
    impressedItems: 0,
    impressionRate: 0,
  });

  // 실제 구현에서는 전역 상태나 로컬 스토리지에서 통계 수집
  // 여기서는 기본 구조만 제공

  return stats;
}
